"use client"

import { useMemo, useRef, useState, useCallback } from "react"
import { GitBranch, GripHorizontal } from "lucide-react"
import { ROLE_COLORS, DEFAULT_ROLE_COLOR } from "@/lib/community/showcase-constants"
import type { CommunityPost } from "@/lib/community/types"

type WorkflowNode = NonNullable<CommunityPost["workflowNodes"]>[number]

// ── Topological sort into layers (columns for horizontal layout) ──

function computeLayers(nodes: WorkflowNode[]): WorkflowNode[][] {
  if (nodes.length === 0) return []

  const idSet = new Set(nodes.map((n) => n.id))
  const inDegree = new Map<string, number>()
  const adjacency = new Map<string, string[]>()

  for (const node of nodes) {
    inDegree.set(node.id, 0)
    adjacency.set(node.id, [])
  }

  for (const node of nodes) {
    for (const dep of node.dependencies) {
      if (idSet.has(dep)) {
        adjacency.get(dep)!.push(node.id)
        inDegree.set(node.id, (inDegree.get(node.id) ?? 0) + 1)
      }
    }
  }

  const layers: WorkflowNode[][] = []
  let queue = nodes.filter((n) => (inDegree.get(n.id) ?? 0) === 0)
  const visited = new Set<string>()

  while (queue.length > 0) {
    layers.push(queue)
    const nextQueue: WorkflowNode[] = []

    for (const node of queue) {
      visited.add(node.id)
      for (const childId of adjacency.get(node.id) ?? []) {
        const newDeg = (inDegree.get(childId) ?? 1) - 1
        inDegree.set(childId, newDeg)
        if (newDeg === 0) {
          const child = nodes.find((n) => n.id === childId)
          if (child && !visited.has(child.id)) nextQueue.push(child)
        }
      }
    }

    queue = nextQueue
  }

  const remaining = nodes.filter((n) => !visited.has(n.id))
  if (remaining.length > 0) layers.push(remaining)

  return layers
}

// ── Node constants ──

const NODE_W = 116
const NODE_H = 36
const ACCENT_W = 3        // left color accent bar
const H_GAP = 56          // gap between columns
const V_GAP = 18          // gap between rows
const PAD_X = 24
const PAD_Y = 24
const ARROW_SIZE = 5
const CONTAINER_H = 260
const DOT_GAP = 14
const DOT_R = 1

interface WorkflowShowcaseProps {
  nodes: NonNullable<CommunityPost["workflowNodes"]>
}

export function WorkflowShowcase({ nodes }: WorkflowShowcaseProps) {
  const layers = useMemo(() => computeLayers(nodes), [nodes])

  const positions = useMemo(() => {
    const pos = new Map<string, { x: number; y: number }>()
    const maxRows = layers.reduce((max, l) => Math.max(max, l.length), 0)

    let x = PAD_X
    for (const layer of layers) {
      const totalH = layer.length * NODE_H + (layer.length - 1) * V_GAP
      const maxTotalH = maxRows * NODE_H + (maxRows - 1) * V_GAP
      let y = PAD_Y + (maxTotalH - totalH) / 2
      for (const node of layer) {
        pos.set(node.id, { x, y })
        y += NODE_H + V_GAP
      }
      x += NODE_W + H_GAP
    }
    return pos
  }, [layers])

  const maxRows = layers.reduce((max, l) => Math.max(max, l.length), 0)
  const svgW = PAD_X * 2 + layers.length * NODE_W + (layers.length - 1) * H_GAP
  const svgH = PAD_Y * 2 + maxRows * NODE_H + (maxRows - 1) * V_GAP

  const edges: { from: string; to: string }[] = []
  for (const node of nodes) {
    for (const dep of node.dependencies) {
      if (positions.has(dep)) {
        edges.push({ from: dep, to: node.id })
      }
    }
  }

  // ── Pan state ──
  const containerRef = useRef<HTMLDivElement>(null)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const dragRef = useRef<{ dragging: boolean; startX: number; startY: number; startPanX: number; startPanY: number }>({
    dragging: false, startX: 0, startY: 0, startPanX: 0, startPanY: 0,
  })

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (e.button !== 0) return
    dragRef.current = { dragging: true, startX: e.clientX, startY: e.clientY, startPanX: pan.x, startPanY: pan.y }
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
  }, [pan])

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragRef.current.dragging) return
    const dx = e.clientX - dragRef.current.startX
    const dy = e.clientY - dragRef.current.startY
    setPan({ x: dragRef.current.startPanX + dx, y: dragRef.current.startPanY + dy })
  }, [])

  const onPointerUp = useCallback(() => {
    dragRef.current.dragging = false
  }, [])

  const resetPan = useCallback(() => setPan({ x: 0, y: 0 }), [])

  if (nodes.length === 0) return null

  const first = nodes[0]?.name ?? "开始"
  const last = nodes[nodes.length - 1]?.name ?? "完成"
  const needsPan = svgW > 400 || svgH > CONTAINER_H

  return (
    <div className="mb-5 space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
          <GitBranch className="h-3.5 w-3.5 text-primary" />
          工作流程图
        </h4>
        {needsPan && (
          <span className="text-micro text-foreground/40 flex items-center gap-1">
            <GripHorizontal className="h-3 w-3" />
            拖动画布
          </span>
        )}
      </div>
      <p className="text-caption text-foreground/60">
        共 {nodes.length} 个节点，从「{first}」到「{last}」
      </p>
      <div
        ref={containerRef}
        className="rounded-xl border border-border/60 overflow-hidden relative select-none"
        style={{
          height: Math.min(svgH + 2, CONTAINER_H),
          cursor: needsPan ? "grab" : "default",
          background: `var(--color-background, #fff)`,
          backgroundImage: `radial-gradient(circle, color-mix(in srgb, var(--color-foreground, #000) 8%, transparent) ${DOT_R}px, transparent ${DOT_R}px)`,
          backgroundSize: `${DOT_GAP}px ${DOT_GAP}px`,
          backgroundPosition: "0 0",
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
      >
        <svg
          width={svgW}
          height={svgH}
          viewBox={`0 0 ${svgW} ${svgH}`}
          className="block"
          style={{ transform: `translate(${pan.x}px, ${pan.y}px)` }}
        >
          <defs>
            {/* Node shadow filter */}
            <filter id="wf-node-shadow" x="-4%" y="-8%" width="108%" height="120%">
              <feDropShadow dx="0" dy="1" stdDeviation="2" floodOpacity="0.06" />
            </filter>
            {/* Arrow marker */}
            <marker
              id="showcase-arrowhead"
              markerWidth={ARROW_SIZE}
              markerHeight={ARROW_SIZE}
              refX={ARROW_SIZE}
              refY={ARROW_SIZE / 2}
              orient="auto"
            >
              <polygon
                points={`0 0, ${ARROW_SIZE} ${ARROW_SIZE / 2}, 0 ${ARROW_SIZE}`}
                className="fill-foreground/20"
              />
            </marker>
          </defs>

          {/* Edges — horizontal bezier curves */}
          {edges.map(({ from, to }, i) => {
            const fromPos = positions.get(from)
            const toPos = positions.get(to)
            if (!fromPos || !toPos) return null
            const x1 = fromPos.x + NODE_W
            const y1 = fromPos.y + NODE_H / 2
            const x2 = toPos.x
            const y2 = toPos.y + NODE_H / 2
            const dx = Math.abs(x2 - x1)
            const cx1 = x1 + dx * 0.4
            const cx2 = x2 - dx * 0.4
            return (
              <path
                key={i}
                d={`M ${x1} ${y1} C ${cx1} ${y1}, ${cx2} ${y2}, ${x2} ${y2}`}
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
                className="text-foreground/15"
                markerEnd="url(#showcase-arrowhead)"
              />
            )
          })}

          {/* Nodes — compact card with left color accent */}
          {nodes.map((node) => {
            const pos = positions.get(node.id)
            if (!pos) return null
            const color = ROLE_COLORS[node.role] ?? DEFAULT_ROLE_COLOR
            return (
              <g key={node.id} filter="url(#wf-node-shadow)">
                {/* Card background */}
                <rect
                  x={pos.x}
                  y={pos.y}
                  width={NODE_W}
                  height={NODE_H}
                  rx={8}
                  className="fill-background"
                  stroke="currentColor"
                  strokeWidth={0.5}
                  style={{ stroke: "var(--color-border)" }}
                />
                {/* Left accent bar */}
                <rect
                  x={pos.x}
                  y={pos.y + 6}
                  width={ACCENT_W}
                  height={NODE_H - 12}
                  rx={1.5}
                  fill={color.text}
                  opacity={0.7}
                />
                {/* Node name */}
                <text
                  x={pos.x + ACCENT_W + 8}
                  y={pos.y + 15}
                  textAnchor="start"
                  className="fill-foreground"
                  style={{ fontSize: 10.5, fontWeight: 600 }}
                >
                  {node.name.length > 8 ? node.name.slice(0, 7) + "…" : node.name}
                </text>
                {/* Role label */}
                <text
                  x={pos.x + ACCENT_W + 8}
                  y={pos.y + 28}
                  textAnchor="start"
                  style={{ fontSize: 8.5, fill: color.text, opacity: 0.65 }}
                >
                  {node.role}
                </text>
              </g>
            )
          })}
        </svg>

        {/* Reset button */}
        {needsPan && (pan.x !== 0 || pan.y !== 0) && (
          <button
            onClick={resetPan}
            className="absolute bottom-2 right-2 text-micro text-foreground/50 hover:text-foreground/80 bg-background/80 backdrop-blur-sm rounded-md px-2 py-0.5 border border-border/60 transition-colors"
          >
            复位
          </button>
        )}
      </div>
    </div>
  )
}
